import Modal from "@/components/base/Modal";
import Badge from "@/components/base/Badge";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_MODE_PAIEMENT_LABELS,
  agenceContratReference,
  agenceLocataireNomComplet,
  agencePaiementReference,
  type AgencePaiement,
} from "@/pages/agence/data/types";
import { monthLabel } from "@/pages/agence/loyers/periods";
import { formatDate, formatFcfa } from "@/utils/format";

interface RecuModalProps {
  paiement: AgencePaiement | null;
  onClose: () => void;
}

export default function RecuModal({ paiement, onClose }: RecuModalProps) {
  const { getContrat, getBien, getImmeuble, getLocataire } = useAgenceData();

  if (!paiement) return null;

  const contrat = getContrat(paiement.contratId);
  const bien = contrat ? getBien(contrat.bienId) : undefined;
  const immeuble = contrat ? getImmeuble(contrat.immeubleId) : undefined;
  const locataire = contrat ? getLocataire(contrat.locataireId) : undefined;

  const lignes: { label: string; value: string }[] = [
    { label: "Reçu n°", value: agencePaiementReference(paiement.id) },
    { label: "Date du paiement", value: formatDate(paiement.date) },
    { label: "Période concernée", value: monthLabel(paiement.periode) },
    { label: "Locataire", value: locataire ? agenceLocataireNomComplet(locataire) : "Non renseigné" },
    {
      label: "Bien",
      value: bien ? `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}` : "Non renseigné",
    },
    { label: "Immeuble", value: immeuble ? immeuble.nom : "Non renseigné" },
    { label: "Contrat", value: contrat ? agenceContratReference(contrat.id) : "Non renseigné" },
    { label: "Mode de paiement", value: AGENCE_MODE_PAIEMENT_LABELS[paiement.mode] },
    { label: "Référence", value: paiement.reference || "—" },
  ];

  return (
    <Modal
      open={Boolean(paiement)}
      onClose={onClose}
      size="md"
      title="Reçu de paiement"
      description="Justificatif d'encaissement du loyer."
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-lg border border-background-200 bg-background-100 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-foreground-500">Montant encaissé</p>
              <p className="mt-1 font-heading text-2xl font-semibold text-foreground-950">
                {formatFcfa(paiement.montant)}
              </p>
            </div>
            <Badge tone="secondary" icon="ri-checkbox-circle-line">
              Encaissé
            </Badge>
          </div>
        </div>

        <dl className="flex flex-col divide-y divide-background-200 rounded-lg border border-background-200">
          {lignes.map((ligne) => (
            <div key={ligne.label} className="flex items-center justify-between gap-4 px-4 py-2.5">
              <dt className="text-xs text-foreground-500">{ligne.label}</dt>
              <dd className="text-right text-sm font-label font-medium text-foreground-950">{ligne.value}</dd>
            </div>
          ))}
        </dl>

        {paiement.note && (
          <div className="rounded-md border border-background-200 bg-background-50 px-4 py-3">
            <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">Note</p>
            <p className="mt-1 text-sm leading-relaxed text-foreground-700">{paiement.note}</p>
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 border-t border-background-200 pt-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Fermer
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-printer-line text-base" aria-hidden="true" />
            Imprimer le reçu
          </button>
        </div>
      </div>
    </Modal>
  );
}